/**
 * Pannello staff: il filtro spam su tutte le caselle. Si guarda quante stanno
 * in ogni modo, cosa ha fatto il filtro negli ultimi giorni, e si può passare
 * tutte le caselle a un modo con un tasto, dopo aver visto la prova a secco.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/client';
import { adminApi, type ModoSpam } from '@/lib/api/endpoints';

const NOME: Record<ModoSpam, string> = { off: 'Spento', suggest: 'Solo segnalazioni', prudent: 'Prudente', auto: 'Automatico' };

export function SpamShieldAdmin() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-spam'], queryFn: () => adminApi.spamOverview(), refetchInterval: 60_000 });
  const cambia = useMutation({
    mutationFn: ({ mode, only }: { mode: ModoSpam; only?: ModoSpam }) => adminApi.spamSetMode(mode, only),
    onSuccess: (r, v) => {
      void qc.invalidateQueries({ queryKey: ['admin-spam'] });
      toast.success(`${r.updated} caselle passate a «${NOME[v.mode]}»`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Operazione non riuscita'),
  });

  function passaTutte(mode: ModoSpam, only?: ModoSpam) {
    const n = q.data ? (only ? q.data.modes[only] : Object.values(q.data.modes).reduce((a, b) => a + b, 0)) : 0;
    const quali = only ? `le ${n} caselle in «${NOME[only]}»` : `tutte le ${n} caselle`;
    if (confirm(`Passare ${quali} a «${NOME[mode]}»?`)) cambia.mutate({ mode, only });
  }

  const d = q.data;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Filtro spam</CardTitle>
        <CardDescription>
          Le caselle esistenti partono da «Solo segnalazioni»: il filtro osserva e non sposta niente. Dopo aver controllato i giudizi con la prova a secco, si passa a «Prudente».
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!d ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              {(['off', 'suggest', 'prudent', 'auto'] as ModoSpam[]).map((m) => (
                <div key={m} className="rounded-md border p-3">
                  <div className="text-2xl font-semibold">{d.modes[m]}</div>
                  <div className="text-xs text-muted-foreground">{NOME[m]}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Ultimi 7 giorni: {d.activity.filtered7d} spostati nello spam, {d.activity.rescued7d} rimessi in arrivo,
              {' '}{d.activity.warnings7d} avvisi in arrivo, {d.activity.aiCalls7d} giudizi dell’AI. Da controllare ora: {d.activity.toReview}.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => passaTutte('prudent', 'suggest')} disabled={cambia.isPending || d.modes.suggest === 0}>
                Passa a Prudente le caselle in osservazione ({d.modes.suggest})
              </Button>
              <Button size="sm" variant="outline" onClick={() => passaTutte('suggest')} disabled={cambia.isPending}>
                Riporta tutte a Solo segnalazioni
              </Button>
              <Button size="sm" variant="outline" onClick={() => passaTutte('off')} disabled={cambia.isPending}>
                Spegni su tutte
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
